import React, { memo, useCallback, useMemo } from "react";
import { Box, Typography, Paper, alpha } from "@mui/material";
import { ScheduleItem } from "../types/scheduleItem.types";
import { TimeScale } from "../types/calendar.types";
import {
  generateTimeSlots,
  getPixelsPerHour,
  getSlotsPerHour,
  formatDate,
  isToday,
  snapToTimeSlot,
  isSameDay,
} from "../utils/timeUtils";
import {
  calculateOverlapLayout,
  PositionedScheduleItem,
} from "../utils/overlapLayout";
import { ScheduleItemCard } from "./ScheduleItemCard";

interface DayColumnProps {
  date: Date;
  items: ScheduleItem[];
  timeScale: TimeScale;
  isFirstColumn?: boolean;
  isLastColumn?: boolean;
  showHeader?: boolean;
  onItemClick?: (item: ScheduleItem) => void;
  onItemDragStart?: (e: React.DragEvent, item: ScheduleItem) => void;
  onItemDragEnd?: () => void;
  onDrop?: (e: React.DragEvent, date: Date, time: Date) => void;
  onCreateClick?: (date: Date, time: Date) => void;
  allowDrag?: boolean;
  onOpenWorkOrder?: (invoiceDocId: number) => void;
}

export const DayColumn: React.FC<DayColumnProps> = memo(
  ({
    date,
    items,
    timeScale,
    isFirstColumn,
    isLastColumn,
    showHeader = true,
    onItemClick,
    onItemDragStart,
    onItemDragEnd,
    onDrop,
    onCreateClick,
    allowDrag = true,
    onOpenWorkOrder,
  }) => {
    const slots = generateTimeSlots(timeScale);
    const pixelsPerHour = getPixelsPerHour(timeScale);
    const slotHeight = pixelsPerHour / (60 / timeScale);
    const today = isToday(date);

    // Filter items that belong to this day and calculate positioning
    const positionedItems = useMemo(() => {
      const dayItems = items.filter((item) => isSameDay(item.startDateTime, date));
      return calculateOverlapLayout(dayItems, timeScale, 0);
    }, [items, date, timeScale]);

    const handleDragOver = useCallback((e: React.DragEvent) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
    }, []);

    const handleDrop = useCallback(
      (e: React.DragEvent) => {
        e.preventDefault();
        const rect = e.currentTarget.getBoundingClientRect();
        const y = e.clientY - rect.top;

        // Calculate time from y position
        // Each slot represents `timeScale` minutes
        const slotIndex = Math.floor(y / slotHeight);
        const totalMinutes = slotIndex * timeScale;
        const hour = Math.floor(totalMinutes / 60);
        const minute = totalMinutes % 60;

        const dropTime = new Date(date);
        dropTime.setHours(hour, minute, 0, 0);

        const snappedTime = snapToTimeSlot(dropTime, timeScale);
        onDrop?.(e, date, snappedTime);
      },
      [date, slotHeight, timeScale, onDrop],
    );

    const handleClick = useCallback(
      (e: React.MouseEvent) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const y = e.clientY - rect.top;

        // Calculate time from y position
        // Each slot represents `timeScale` minutes
        const slotIndex = Math.floor(y / slotHeight);
        const totalMinutes = slotIndex * timeScale;
        const hour = Math.floor(totalMinutes / 60);
        const minute = totalMinutes % 60;

        const clickTime = new Date(date);
        clickTime.setHours(hour, minute, 0, 0);

        const snappedTime = snapToTimeSlot(clickTime, timeScale);
        onCreateClick?.(date, snappedTime);
      },
      [date, slotHeight, timeScale, onCreateClick],
    );

    return (
      <Box
        sx={{
          flex: 1,
          minWidth: 120,
          borderRight: isLastColumn ? "none" : 1,
          borderColor: "divider",
          display: "flex",
          flexDirection: "column",
          bgcolor: "background.paper",
          position: "relative",
        }}
      >
        {/* Day header - only show if showHeader is true */}
        {showHeader && (
          <Paper
            elevation={today ? 2 : 0}
            sx={{
              height: 50,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              borderBottom: 1,
              borderColor: "divider",
              bgcolor: today ? "primary.light" : "background.paper",
              borderRadius: 0,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                color: today ? "primary.contrastText" : "text.secondary",
                textTransform: "uppercase",
                fontSize: "0.75rem",
              }}
            >
              {date.toLocaleDateString("en-US", { weekday: "short" })}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontWeight: today ? 700 : 400,
                color: today ? "primary.contrastText" : "text.primary",
              }}
            >
              {formatDate(date, "short")}
            </Typography>
            {today && (
              <Box
                sx={{
                  position: "absolute",
                  bottom: 4,
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  bgcolor: "primary.contrastText",
                }}
              />
            )}
          </Paper>
        )}

        {/* Time slots grid */}
        <Box
          sx={{
            flex: 1,
            position: "relative",
            cursor: "pointer",
          }}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={handleClick}
        >
          {/* Grid lines */}
          {slots.map((_, index) => (
            <Box
              key={index}
              sx={{
                height: slotHeight,
                borderBottom: index < slots.length - 1 ? "1px solid" : "none",
                borderColor: (theme) =>
                  index % (60 / timeScale) === 0
                    ? "divider"
                    : alpha(theme.palette.divider, 0.5),
                boxSizing: "border-box",
              }}
            />
          ))}

          {/* Current time indicator (if today) */}
          {today && <CurrentTimeIndicator timeScale={timeScale} />}

          {/* Schedule items — WO-linked items are fixed in place (no drag) */}
          {positionedItems.map((item) => (
            <ScheduleItemCard
              key={item.scheduleItemId || `wo-${item.workOrderId}`}
              item={item}
              onClick={() => onItemClick?.(item)}
              onDragStart={onItemDragStart}
              onDragEnd={onItemDragEnd}
              allowDrag={allowDrag && !item.workOrderId}
              onOpenWorkOrder={onOpenWorkOrder}
            />
          ))}
        </Box>
      </Box>
    );
  },
);

DayColumn.displayName = "DayColumn";

// Current time indicator component

const CurrentTimeIndicator: React.FC<{ timeScale: TimeScale }> = ({
  timeScale,
}) => {
  const [now, setNow] = React.useState(new Date());

  React.useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 60000); // Update every minute
    return () => clearInterval(interval);
  }, []);

  const top = calculateTopPosition(now, timeScale, 0);

  return (
    <Box
      sx={{
        position: "absolute",
        left: 0,
        right: 0,
        top: top,
        height: 2,
        bgcolor: "error.main",
        zIndex: 5,
        pointerEvents: "none",
        "&::before": {
          content: '""',
          position: "absolute",
          left: -4,
          top: -3,
          width: 10,
          height: 10,
          borderRadius: "50%",
          bgcolor: "error.main",
        },
      }}
    />
  );
};

// Helper function for top position
function calculateTopPosition(
  date: Date,
  timeScale: TimeScale,
  dayStartHour: number,
): number {
  const hour = date.getHours();
  const minute = date.getMinutes();
  const pixelsPerHour = getPixelsPerHour(timeScale);

  const hoursFromStart = hour - dayStartHour;
  const minuteFraction = minute / 60;

  return (hoursFromStart + minuteFraction) * pixelsPerHour;
}
